import { ICharacter } from "@next-platform/client-common-core";
import { CharacterCardActions, CharacterCardVariant } from "@next-platform/client-common-spicy-ui";
import React, { Dispatch, SetStateAction } from "react";
import InstantSearchAdapter from "typesense-instantsearch-adapter";

import { Collection } from "@/types/api";

export type SearchBarProps = {
  queryBy: string;
  setQueryBy: Dispatch<SetStateAction<string>>;
  defaultQueryBy: string;
  /** The rulebook index doesn't index `creator_username`, so the `@creator` mode is disabled there. */
  isCreatorSearchEnabled?: boolean;
  setFoundNegativeKeyword: Dispatch<SetStateAction<boolean>>;
};

export interface IRefinementListProps {
  attribute: string;
  operator: "and" | "or" | undefined;
  limit?: number;
}

export type NsfwToggleProps = {
  showNSFW: boolean;
  isNsfwToggled: boolean;
  setNsfwToggle: (value: boolean) => void;
};

export type SearchBySortProps = {
  collection?: Collection;
  baseIndexName: string;
  currentRefinement: string;
  setCurrentCategory: Dispatch<SetStateAction<string>>;
};

export enum DataSourceType {
  SHARED_CHATBOTS = "sharedChatbots",
  MY_CHATBOTS = "myChatbots",
  FAVOURITES = "favourites",
}

export type DataSourceDropdownProps = {
  currentSource: DataSourceType;
  onSourceChange: (source: DataSourceType) => void;
  showDropdown: boolean;
};

export type SearchTagFilterTriggerProps = {
  tagListMinimized: boolean;
  setTagListMinimized: (value: boolean) => void;
  setTagsDrawer: Dispatch<SetStateAction<boolean>>;
  onlyDesktop?: boolean;
  notDesktop?: boolean;
};

export type SearchAdapter = InstantSearchAdapter & {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- external adapter config requires snake_case keys
  configuration: { additionalSearchParameters: { query_by: string; include_fields: string } };
};

export enum CharacterCardDropdownActions {
  ATTACH = "Attach",
  SHARE = "Share",
  BLOCK = "Block",
  REPORT = "Report",
  LESS_LIKE_THIS = "LessLikeThis",
  CLONE = "Clone",
}

export enum CharacterItemAction {
  ADD = "add",
  REMOVE = "remove",
}

// Enum for header component types
export enum HeaderComponentType {
  BANNER = "banner",
  GROUP_CHAT = "groupChat",
  CUSTOM = "custom",
  NONE = "none",
}

// Enum for display context
export enum DisplayContext {
  SHARED_CHATBOTS = "sharedChatbots",
  MY_CHATBOTS = "myChatbots",
  FAVORITES = "favorites",
  MEMBER_SELECT = "memberSelect",
}

// Enum for view mode - how characters are displayed
export enum ViewMode {
  CARD = "card",
  HORIZONTAL_SELECTABLE = "horizontal_selectable",
}

export type BaseSearchListingConfig = {
  headerComponent?: HeaderComponentType;
  viewMode?: ViewMode;
  customHeader?: React.ReactNode;
  hideNsfwToggle?: boolean;
  hideWaitingQueue?: boolean;
  hideBannedBanner?: boolean;
  showDataSourceDropdown?: boolean;
};

export type MemberSelectConfig = BaseSearchListingConfig & {
  context: DisplayContext.MEMBER_SELECT;
  onItemAction: (action: CharacterItemAction, character: ICharacter | undefined) => void;
  maxItemsToSelect: number;
  selectedItems: string[];
  onLimitReached?: (maxCount: number) => void;
  excludedCharacterIds?: string[];
};

export type BrowsingConfig = BaseSearchListingConfig & {
  context?: DisplayContext.SHARED_CHATBOTS | DisplayContext.MY_CHATBOTS | DisplayContext.FAVORITES;
  onItemAction?: (action: CharacterItemAction, character: ICharacter | undefined) => void;
};

export type SearchListingConfig = MemberSelectConfig | BrowsingConfig;

export type CharacterCardData = {
  isHidden: boolean;
  characterAvatarBlur: boolean;
  characterId: string;
  characterName: string;
  characterVisibility?: string;
  visibilityBadgeLabel?: string;
  characterDefinitionVisible: boolean;
  characterCreatorUserName: string;
  characterTitle: string;
  characterTags: string[];
  characterMessageNumber: number;
  characterRatingScore: number;
  characterTokensCount: number;
  characterLikeLabel: string;
  characterInfoLabel: string;
  characterAvatarUrl: string;
  characterImageGen: boolean;
  characterImageGenLabel: string;
  hasVoice?: boolean;
  characterCardVariant: CharacterCardVariant;
  creatorPagePath: string;
  chatPagePath: string;
  chatbotProfilePagePath: string;
  lorebookAttached?: boolean;
  lorebookLabel?: string;
  characterActionsState: {
    [CharacterCardActions.FAVORITE]: {
      isDisabled: boolean;
      isHidden: boolean;
      isLiked: boolean;
    };
  };
};
